import React, { useState, useEffect, useCallback } from 'react';
import { FitoListReactQuery } from '~/Components';
import { useLanguage, ToastVariant, useToastMessage, useDateFns, useHasAccess } from '~/Hooks';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import YogaImg from '~/Assets/Images/SportsIllustrations/Yoga.svg';
import { useAttendance } from './useAttendance';
import { Trans } from 'react-i18next';
import { Filter, FilterGroups } from '~/Components/Filter';
import { useCheckinFilter } from './useAttendanceFilter';
import { Button, DateRangePicker } from '~/UIKit';
import { usePrivateLayoutTitle } from '~/Layout/PrivateLayout/PrivateLayoutTitle/usePrivateLayoutTitle';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { RouteParams } from '~/Routes/PrivateRoutes';
import { Route } from '~/Constants/Routes.constants';
import { ManageBookingsMenu } from '~/Components/ManageBookingsMenu/ManageBookingsMenu';
import { IAttendance } from '~/Models/DTO/Attendance.model';
import { useRequestAttendanceList } from '~/requests/coreService/useRequestAttendanceList';
import { AttendanceStyles } from './Attendance.styles';
import { useRequestPendingBookingsSettings } from '~/requests/coreService/useRequestPendingBookingsSettings';

export default function Attendance() {
  const { t } = useLanguage();
  const { formatDate } = useDateFns();
  const [lastRecord, setRecord] = useState<IAttendance>();
  const [activateLast, setActive] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [date, setDate] = useState([new Date(), new Date()]);
  const { classes } = AttendanceStyles();
  const { keyOfId, setItemValues, settings, tableDefinition } = useAttendance();
  const loc = useLocation();
  const { showToast } = useToastMessage();
  const { hasSuperAdminAccess } = useHasAccess();
  const currentPage = useRouteMatch<RouteParams>({
    path: Route.ATTENDANCE,
    location: loc,
  });
  const isAttendancePage = currentPage?.isExact;
  const {
    data: pendingBookingsSettings,
    isLoading: isPendingBookingsSettingsLoading,
  } = useRequestPendingBookingsSettings();
  const [shouldShowManageBookingsButton, setShouldShowManageBookingsButton] = useState<
    Boolean | undefined
  >(false);

  const { bookingsMenuEl, handleOpenManageBookingsMenu, handleCloseMenu } = usePrivateLayoutTitle();
  const clearActive = useCallback(() => {
    setActive(0);
    setRecord(undefined);
  }, []);

  const defaultQuery = {
    page: 0,
    pageSize: 1000,
  };

  const {
    open,
    groups,
    onOpen,
    onClose,
    onClear,
    onApply,
    selectedCount,
    appliedFilters,
  } = useCheckinFilter({
    additionalfilters: {
      activateLast: activateLast ? 'active' : undefined,
      searchText,
      date,
    },
  });

  const checkInResponse = useRequestAttendanceList({ ...appliedFilters, ...defaultQuery });
  const { data, isLoading, totalCount, newErroredBookings, setErroredBookings } = checkInResponse;

  const pageSize = data?.length > 10 ? data?.length + 1 : 10;

  const shouldShowButton =
    pendingBookingsSettings?.manualCheckInIsAllowed ||
    pendingBookingsSettings?.manualNoShowIsAllowed ||
    pendingBookingsSettings?.manualLateCancellationIsAllowed;

  const onDateChange = useCallback((values) => {
    setDate(values);
    setErroredBookings(null);
    clearActive();
  }, []);

  useEffect(() => {
    if (isLoading) {
      setActive(0);
    }
  }, [isLoading]);

  useEffect(() => {
    if (newErroredBookings && newErroredBookings.length) {
      newErroredBookings.forEach((booking: IAttendance) => {
        showToast(
          `NewErroredManualBooking_${booking.customerName}`,
          <div className={classes.textWrapper}>
            <Trans
              i18nKey="app.checkin.pendingBookings.error"
              values={{ emailOrMemberId: booking.customerName, failureReason: booking.error }}
              components={{ b: <b />, break: <br /> }}
            />
          </div>,
          ToastVariant.ERROR,
          true,
          undefined,
          10000,
        );
      });
      setRecord(undefined);
    }
  }, [newErroredBookings]);

  useEffect(() => {
    // 1. should be always visible for super admins
    // 2. should be visible only if the user has access to the attendance page
    // and the settings allow it for the non-super-admins users
    const shouldShow = hasSuperAdminAccess || (isAttendancePage && shouldShowButton);
    setShouldShowManageBookingsButton(shouldShow);
  }, [pendingBookingsSettings, hasSuperAdminAccess]);

  useEffect(() => {
    const lastR = data?.filter((item: any) => !item.pendingBookingId)?.[0];
    if (!lastRecord && lastR) {
      setRecord(lastR);
      return;
    }
    if (
      data?.length &&
      lastR &&
      lastRecord?.checkInTime !== lastR?.checkInTime &&
      lastRecord?.checkInStatus !== 'Errored'
    ) {
      setRecord(lastR);
      setActive(1);
      if (['CheckedIn', 'NoShow', 'LateCancellation'].includes(lastR.bookingState)) {
        const checkinMessage = {
          CheckedIn: 'app.checkin.toast',
          NoShow: 'app.checkin.pendingBookings.noShow',
          LateCancellation: 'app.checkin.pendingBookings.lateCancellation',
        } as any;
        toastToDisplay(checkinMessage[lastR.bookingState], lastR);
      }
    }
  }, [data]);

  const toastToDisplay = (toastMessage: string, data: any) =>
    showToast(
      `NewCheckIn_${data.customerName}_${data.checkInTime}`,
      <Trans
        i18nKey={t(toastMessage, {
          customerName: data.customerName,
          eventName: data.eventName,
          checkInTime: formatDate(data.checkInTime.toString(), 'HH:mm'),
        })}
        components={{ b: <b /> }}
      />,
      ToastVariant.SUCCESS,
      false,
      undefined,
      7000,
    );

  const filterComponent = (
    <div className={classes.filterWraper}>
      <DateRangePicker onDateChange={onDateChange} defaultValue={[new Date(), new Date()]} />
      <Filter
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        onClear={onClear}
        onApply={() => {
          onApply();
          clearActive();
        }}
        selectedCount={selectedCount}
      >
        <FilterGroups groups={groups} />
      </Filter>
    </div>
  );

  const backendSearch = (txt: string) => {
    if (txt !== searchText) {
      clearActive();
    }
    setSearchText(txt);
    return checkInResponse;
  };

  const manageAttendanceComponent = shouldShowManageBookingsButton ? (
    <div className={classes.buttonsWrapper}>
      <Button
        size="medium"
        variant="outlined"
        onClick={handleOpenManageBookingsMenu}
        className={classes.manageBookings}
      >
        {t('app.events.manualCheckin.button')}
      </Button>
      <ManageBookingsMenu
        pendingBookingsSettings={pendingBookingsSettings!}
        isLoading={isPendingBookingsSettingsLoading}
        anchorEl={bookingsMenuEl}
        handleCloseMenu={handleCloseMenu}
      />
    </div>
  ) : null;

  return (
    <PrivateLayout
      title={`${t('app.checkin.title')} : ${totalCount}`}
      hideBackButton
      articleLink="app.checkinPageLink"
      secondaryTitleActionElement={manageAttendanceComponent}
    >
      <FitoListReactQuery<IAttendance>
        title={t('app.checkin.list')}
        pageSize={pageSize}
        keyOfId={keyOfId}
        settings={settings}
        setItemValues={setItemValues}
        tableDefinition={tableDefinition}
        queryResults={() => checkInResponse}
        defaultQuery={defaultQuery}
        appliedFilters={appliedFilters}
        backendSearch={backendSearch}
        searchTextPlaceholder={t('app.search.checkin')}
        placeholder={{
          imgSrc: YogaImg,
          imgAlt: t('app.payouts.emptyState'),
          title: t('app.payouts.emptyState'),
        }}
        filterComponent={filterComponent}
      />
    </PrivateLayout>
  );
}
